import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Copy, Plus, CheckCircle2, XCircle, Pencil, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { referralService } from '@/services/referralService';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

type ReferralRole = 'growth_agent' | 'ambassador' | 'affiliate' | 'partner';

interface ReferralSource {
  id: string;
  name: string;
  email: string;
  role: ReferralRole;
  referral_code: string;
  is_active: boolean;
  default_rate_table_id: string | null;
}

export default function AdminReferralSources() {
  const { session, user } = useAdminAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<ReferralSource | null>(null);
  const [sendingEmailTo, setSendingEmailTo] = useState<string | null>(null);
  const [newSource, setNewSource] = useState<{ 
    name: string; 
    email: string; 
    role: ReferralRole;
    rateTableId?: string;
  }>({ 
    name: '', 
    email: '', 
    role: 'growth_agent',
    rateTableId: undefined
  });

  const { data: sources, refetch } = useQuery({
    queryKey: ['referral-sources'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_referral_sources');
      if (error) throw error;
      return data as ReferralSource[];
    }
  });

  const { data: rateTables } = useQuery({
    queryKey: ['rate-tables'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rate_tables')
        .select('id, name, is_default')
        .order('is_default', { ascending: false });
      if (error) throw error;
      return data;
    }
  });

  // Debug session on mount
  useEffect(() => {
    console.log('Admin Referral Sources - Auth Status:', {
      hasSession: !!session,
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.email
    });
  }, [session, user]);

  const handleAdd = async () => {
    if (!newSource.name || !newSource.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!session || !user) {
      toast.error('Authentication session expired. Please log in again.');
      return;
    }

    const { data: currentSession } = await supabase.auth.getSession();
    if (!currentSession?.session) {
      toast.error('No active session found. Please log in again.');
      return;
    }

    const { data: diagnostic, error: diagError } = await supabase.rpc('diagnose_admin_auth');
    const diagnosticData = diagnostic as { is_admin: boolean } | null;

    if (diagError || !diagnosticData?.is_admin) {
      toast.error('Admin privileges not detected. Please contact support.');
      return;
    }

    const { data: code, error: codeError } = await supabase.rpc('generate_unique_referral_code', {
      p_name: newSource.name,
      p_role: newSource.role
    });

    if (codeError) {
      toast.error(`Failed to generate code: ${codeError.message}`);
      return;
    }

    const { error } = await supabase.rpc('admin_create_referral_source', {
      p_name: newSource.name,
      p_email: newSource.email,
      p_role: newSource.role,
      p_referral_code: code,
      p_rate_table_id: newSource.rateTableId || null
    });

    if (error) {
      toast.error(`Failed to create referral source: ${error.message}`);
      return;
    }

    toast.success('Referral source created!');
    setIsAddModalOpen(false);
    setNewSource({ name: '', email: '', role: 'growth_agent', rateTableId: undefined });
    refetch();
  };

  const handleEdit = (source: ReferralSource) => {
    setEditingSource({ ...source });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingSource) return;

    if (!editingSource.name || !editingSource.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!session || !user) {
      toast.error('Authentication session expired. Please log in again.');
      return;
    }

    const { data: diagnostic, error: diagError } = await supabase.rpc('diagnose_admin_auth');
    const diagnosticData = diagnostic as { is_admin: boolean } | null;

    if (diagError || !diagnosticData?.is_admin) {
      toast.error('Admin privileges not detected. Please contact support.');
      return;
    }

    const { error } = await supabase
      .from('referral_sources')
      .update({
        name: editingSource.name,
        email: editingSource.email,
        role: editingSource.role,
        is_active: editingSource.is_active,
        default_rate_table_id: editingSource.default_rate_table_id
      })
      .eq('id', editingSource.id);

    if (error) {
      toast.error(`Failed to update partner: ${error.message}`);
      return;
    }

    toast.success('Partner updated successfully!');
    setIsEditModalOpen(false);
    setEditingSource(null);
    refetch();
  };

  const copyLink = (code: string) => {
    const link = referralService.generateReferralLink(code);
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const resendWelcomeEmail = async (source: ReferralSource) => {
    setSendingEmailTo(source.id);
    try {
      const response = await supabase.functions.invoke('send-partner-magic-link', {
        body: { email: source.email }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success(`Welcome email sent to ${source.email}`);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send welcome email');
    } finally {
      setSendingEmailTo(null);
    }
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Referral Sources</h1>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add Source
        </Button>
      </div>

      <div className="grid gap-4">
        {sources?.map((source) => (
          <Card key={source.id} className="p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {source.name}
                  {source.is_active ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">{source.email}</p>
                <p className="text-sm text-muted-foreground capitalize">{source.role?.replace('_', ' ')}</p>
                {source.default_rate_table_id && rateTables && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Rate Table: <span className="font-medium">
                      {rateTables.find(t => t.id === source.default_rate_table_id)?.name || 'Unknown'}
                    </span>
                  </p>
                )}
                <div className="mt-3 flex items-center gap-2">
                  <code className="bg-muted px-3 py-1 rounded text-sm">{source.referral_code}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyLink(source.referral_code)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Input 
                  readOnly 
                  value={referralService.generateReferralLink(source.referral_code)} 
                  className="mt-2 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => resendWelcomeEmail(source)}
                  disabled={sendingEmailTo === source.id || !source.is_active}
                  title={!source.is_active ? 'Partner must be active to send email' : 'Send login link email'}
                >
                  {sendingEmailTo === source.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleEdit(source)}>
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Referral Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input 
              placeholder="Name" 
              value={newSource.name} 
              onChange={(e) => setNewSource({...newSource, name: e.target.value})} 
            />
            <Input 
              placeholder="Email" 
              type="email"
              value={newSource.email} 
              onChange={(e) => setNewSource({...newSource, email: e.target.value})} 
            />
            <Select value={newSource.role} onValueChange={(v: ReferralRole) => setNewSource({...newSource, role: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="growth_agent">Growth Agent</SelectItem>
                <SelectItem value="ambassador">Ambassador</SelectItem>
                <SelectItem value="affiliate">Affiliate</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={newSource.rateTableId || 'default'} 
              onValueChange={(v) => setNewSource({...newSource, rateTableId: v === 'default' ? undefined : v})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select rate table" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default Rate Table</SelectItem>
                {rateTables?.map((table) => (
                  <SelectItem key={table.id} value={table.id}>
                    {table.name} {table.is_default ? '(Default)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAdd} className="w-full">Create Source</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
          </DialogHeader>
          {editingSource && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input 
                  id="edit-name"
                  value={editingSource.name} 
                  onChange={(e) => setEditingSource({...editingSource, name: e.target.value})} 
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input 
                  id="edit-email"
                  type="email"
                  value={editingSource.email} 
                  onChange={(e) => setEditingSource({...editingSource, email: e.target.value})} 
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select 
                  value={editingSource.role} 
                  onValueChange={(v: ReferralRole) => setEditingSource({...editingSource, role: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="growth_agent">Growth Agent</SelectItem>
                    <SelectItem value="ambassador">Ambassador</SelectItem>
                    <SelectItem value="affiliate">Affiliate</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate Table</Label>
                <Select 
                  value={editingSource.default_rate_table_id || 'none'} 
                  onValueChange={(v) => setEditingSource({
                    ...editingSource, 
                    default_rate_table_id: v === 'none' ? null : v
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select rate table" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Rate Table</SelectItem>
                    {rateTables?.map((table) => (
                      <SelectItem key={table.id} value={table.id}>
                        {table.name} {table.is_default ? '(Default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-active">Active Status</Label>
                <Switch 
                  id="edit-active"
                  checked={editingSource.is_active} 
                  onCheckedChange={(checked) => setEditingSource({...editingSource, is_active: checked})}
                />
              </div>
              <div className="text-sm text-muted-foreground">
                <Label>Referral Code</Label>
                <code className="block bg-muted px-3 py-2 rounded mt-1">{editingSource.referral_code}</code>
              </div>
              <Button onClick={handleUpdate} className="w-full">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
